import { t } from '@framework/i18n/intl/i18next.ts';
import { useCallback } from 'react';
import { hasRichTextAdvanced } from '../../../../../inputs/rich-text-advanced/hasRichTextAdvanced.ts';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { AdvancedSelectModalBase } from '../advanced-select-common/components/AdvancedSelectModalBase.tsx';
import { type IWidgetAdvancedOptionValue } from '../advanced-select-common/form/createAdvancedSelectValueSchema.ts';
import { useAdvancedSelectModal } from '../advanced-select-common/hooks/useAdvancedSelectModal.ts';
import { useAdvancedSelectModalLabels } from '../advanced-select-common/hooks/useAdvancedSelectModalLabels.ts';
import { getDinamicButtonProps } from '../advanced-select-common/utils/getDinamicButtonProps.tsx';
import { type IWidgetParsedProps } from '../common/util/getWidgetProps.ts';
import { AdvancedSingleSelectTableData } from './AdvancedSingleSelectTableData.tsx';
import { type IAdvancedSingleSelectWidget } from './IAdvancedSingleSelectWidget.ts';

export interface IAdvancedSingleSelectWidgetModalProps
  extends IWidgetComponentProps<IAdvancedSingleSelectWidget> {
  widgetOptions: IWidgetParsedProps;
  objectFieldValue: IWidgetAdvancedOptionValue | null;
  onChange: (value: IWidgetAdvancedOptionValue | null) => void;
}

/**
 * Componente modale per la selezione singola avanzata
 */
export function AdvancedSingleSelectWidgetModal(
  props: IAdvancedSingleSelectWidgetModalProps,
) {
  const { widget, objectFieldValue, onChange, readonly } = props;

  const isOptionSelected = objectFieldValue != null;

  const { buttonText } = getDinamicButtonProps(
    widget.buttonText,
    widget.buttonTextAfter,
    isOptionSelected,
  );

  const handleClear = useCallback(() => {
    return onChange(null);
  }, [onChange]);

  const {
    open,
    handleOpen,
    handleConfirm,
    handleCancel,
    handleClearSelection,
  } = useAdvancedSelectModal(objectFieldValue, onChange, handleClear);

  const { confirmText, cancelText, clearSelectionText } =
    useAdvancedSelectModalLabels({
      buttonText: widget.buttonText,
      cancelButtonText: widget.cancelButtonText,
    });

  return (
    <AdvancedSelectModalBase
      readonly={readonly}
      buttonText={buttonText}
      open={open}
      onOpen={handleOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      onClearSelection={handleClearSelection}
      showClearSelection={isOptionSelected && !readonly}
      labels={{
        confirm: confirmText,
        cancel: cancelText,
        clearSelection: clearSelectionText,
      }}
      title={
        <RichTextAdvancedRender
          text={
            hasRichTextAdvanced(widget.modalTitle)
              ? widget.modalTitle
              : t(
                  'widgets.advanced-single-select.modal-select-option-title',
                  'Seleziona Opzione',
                )
          }
        />
      }
    >
      <AdvancedSingleSelectTableData {...props} />
    </AdvancedSelectModalBase>
  );
}
